import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import SEO from '@/components/SEO';
import { ADDON_PRICES } from '@/config/pricing';
import { ArrowRight, Check, Palette, FileText, Users, Zap, Target, BarChart3 } from 'lucide-react';
import '@/styles/forms.css';
import '@/styles/landing.css';

const benefits = [
  { icon: FileText, title: 'White-label PDF reports', desc: 'Remove all UXAuditPro branding and deliver fully branded audit reports under your agency name.' },
  { icon: Zap, title: 'Deliver in hours, not weeks', desc: 'Run a comprehensive UX audit in under 5 minutes. Scale your audit services without scaling headcount.' },
  // WAS: "AI analysis trained on thousands of UX patterns delivers findings
  // that rival expensive consultant reports." Cut 2026-08-19 as a verbatim
  // repeat of the /use-cases/ item-2 ruling, with "patterns" swapped for
  // "apps". Nothing in this pipeline trains on anything — the model is a
  // foundation model handed a capture — and "rival expensive consultant
  // reports" is an unevidenced comparative stacked on top of it.
  //
  // The replacement is what actually makes the output consistent: a fixed
  // rubric and evidence attached to each finding.
  { icon: BarChart3, title: 'Expert-level output', desc: 'Every audit runs the same heuristic rubric and cites the evidence behind each finding, so the output does not vary with who ran it.' },
  { icon: Users, title: 'Impress every client', desc: 'Scored dimensions, prioritized findings, sprint roadmaps — a deliverable that makes your agency look world-class.' },
];

const useCases = [
  { title: 'Audit website redesign briefs', desc: 'Before starting a redesign project, run an audit to build a data-driven brief that clients trust.' },
  { title: 'Quarterly UX health checks', desc: 'Offer ongoing retainer clients monthly or quarterly UX audits as a recurring revenue service.' },
  { title: 'Competitive benchmarking', desc: 'Audit a client\'s product alongside key competitors to identify UX gaps and strategic opportunities.' },
  { title: 'Pre-launch validation', desc: 'Catch UX issues before a product launch so your team ships confidence, not costly post-launch fixes.' },
];

export default function UseCasesAgenciesPage() {
  const { login, user } = useAuth();

  return (
    <div>
      <SEO
        fullTitle="White-Label UX Audit Reports Under Your Brand | UXAuditPro"
        description="Resell UX audits under your own brand. Run an AI audit on any client site and send a branded PDF report in minutes, not weeks."
        canonical="/use-cases/agencies"
        ogTitle="White-Label UX Audits for Agencies | UXAuditPro"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'UX Audits for Agencies — White-Label',
          url: 'https://www.uxauditpro.com/use-cases/agencies/',
          description: 'UXAuditPro helps design and product agencies deliver white-label UX audit reports to clients using AI-powered analysis.',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.uxauditpro.com/' },
              { '@type': 'ListItem', position: 2, name: 'Use Cases', item: 'https://www.uxauditpro.com/use-cases/' },
              { '@type': 'ListItem', position: 3, name: 'Agencies', item: 'https://www.uxauditpro.com/use-cases/agencies/' },
            ],
          },
        }}
      />

      {/* Hero */}
      <header className="uxa-pt-xl px-6 hero" style={{ position: 'relative', textAlign: 'center' }}>
        <div className="uxa-hero-grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 1180, margin: '0 auto' }}>
          <span className="section-label inline-block mb-6">Agencies & Studios</span>
          <h1
            className="uxa-h1 mb-6"
            style={{ color: 'var(--ink-1)' }}
          >
            Scale your agency with{' '}<br />
            <span style={{ color: 'var(--accent-display)', fontStyle: 'italic', fontWeight: 700 }}>white-label UX audits</span>
          </h1>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', maxWidth: 620, margin: '20px auto 34px' }}>
            Deliver expert-quality UX audit reports under your own brand. Scale your UX services without hiring specialist consultants.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {user ? (
              <Link to="/audit/new" className="uxa-submit uxa-submit--auto">
                Start Free Agency Audit
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </Link>
            ) : (
              <button onClick={login} className="uxa-submit uxa-submit--auto">
                Start Free Agency Audit
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </button>
            )}
            <Link to="/ux-auditor/#pricing" className="uxa-submit uxa-submit--auto uxa-submit--secondary">
              View Agency Pricing
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </header>

      {/* Benefits */}
      <section className="uxa-pt-xl">
        <div className="uxa-landing-section">
          <span className="section-label block text-center mb-4">Why Agencies Choose UXAuditPro</span>
          <h2
            className="uxa-h2 text-center mb-16"
            style={{ color: 'var(--ink-1)' }}
          >
            Scale your UX practice without the overhead
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((item) => (
              <div key={item.title} className="uxa-feature-card" style={{ display: 'flex', gap: 22 }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--accent-tint)' }}>
                  <item.icon className="w-6 h-6" style={{ color: 'var(--accent-text)' }} />
                </div>
                <div>
                  <h3 className="uxa-h3 mb-2" style={{ color: 'var(--ink-1)' }}>{item.title}</h3>
                  <p className="uxa-dense" style={{ color: 'var(--ink-2)' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="uxa-pt-xl">
        <div className="uxa-landing-section">
          <span className="section-label block text-center mb-4">Agency Use Cases</span>
          <h2
            className="uxa-h2 text-center mb-16"
            style={{ color: 'var(--ink-1)' }}
          >
            How agencies use UXAuditPro
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {useCases.map((item) => (
              <div key={item.title} className="uxa-feature-card">
                <div className="flex items-start gap-3 mb-2">
                  {/* --success is scoped in tokens.css to "form validation
                      only. NEVER a score". A tick on a marketing list is a
                      mark, and marks take the accent. */}
                  <span className="uxa-price-tick" style={{ marginTop: 2, width: 22, height: 22 }} aria-hidden="true"><Check size={12} /></span>
                  <h3 className="uxa-h3" style={{ color: 'var(--ink-1)' }}>{item.title}</h3>
                </div>
                <p className="uxa-dense pl-8" style={{ color: 'var(--ink-2)' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* White-label highlight */}
      <section className="uxa-pt-xl">
        <div className="uxa-landing-section">
          {/* THE PAGE'S ONE TONAL BREAK. It was a white card on a sand band,
              and the closing CTA below was the dark one -- two breaks, where
              the system's pages take one. This is the page's product moment,
              so the break goes here and the CTA becomes the light closing band.
              Same call as /use-cases/ and /use-cases/saas/.

              On this ground --accent-text measures 2.71:1 and fails, so the
              eyebrow and the link take --accent-soft, which is 10.21:1. The
              icon tile keeps --accent-tint: it is a mark, not a substrate a
              reader must read. */}
          <div className="uxa-dark-panel">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--accent-tint)' }}>
                <Palette className="w-6 h-6" style={{ color: 'var(--accent-text)' }} />
              </div>
              <div>
                <p className="text-sm font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--accent-soft)' }}>White-Label Add-on</p>
                <h3 className="uxa-h3" style={{ color: 'var(--text-inverse)' }}>Your brand. Our AI.</h3>
              </div>
            </div>
            <p className="uxa-body mb-6" style={{ color: 'var(--ink-4)' }}>
              {`Add the White-Label Reports add-on for a one-time $${ADDON_PRICES.white_label.usd}. Upload your agency logo, set your brand colors, and every PDF report exports with your branding — no mention of UXAuditPro.`}
            </p>
            <ul className="space-y-2 mb-6">
              {['Custom agency logo on every PDF', 'Brand color customization', 'Remove all UXAuditPro references', 'Client-ready professional output'].map((f) => (
                <li key={f} className="flex items-center gap-3" style={{ fontSize: 'var(--type-dense-size)', color: 'var(--text-inverse)' }}>
                  <span className="uxa-price-tick" style={{ marginTop: 0, width: 20, height: 20 }} aria-hidden="true"><Check size={12} /></span>
                  {f}
                </li>
              ))}
            </ul>
            <Link
              to="/ux-auditor/#pricing"
              /* 44px MINIMUM. Measured at 148x20 on the built page at all four
                 widths — a standalone call to action at the end of a panel, so
                 WCAG 2.5.8's inline-prose exemption does not apply to it. It
                 was 20px tall before this change too; the panel rebuild is
                 simply the first time it was measured. */
              className="inline-flex items-center gap-2 text-sm font-bold"
              style={{ color: 'var(--accent-soft)', minHeight: 44, paddingRight: 4 }}
            >
              See agency pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      {/* ═══ CLOSING CTA ═══════════════════════════════════════════════════════
          Was an --ink-strong band. The page's one tonal break is now the dark
          How It Works panel above, so this takes the homepage's closing shape:
          sand ground, masked 56px grid, spectrum bloom under the primary
          action. The italic accent moved with the ground -- --accent-soft is
          10.21:1 on ink and --accent-text 2.71:1; on sand it reverses, so the
          value swaps and the pair stays AA. */}
      <section className="uxa-closing" style={{ marginTop: 120 }}>
        <div className="uxa-closing__grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <h2
            className="uxa-h2 mb-5"
            style={{ color: 'var(--ink-1)' }}
          >
            Start delivering better client audits.
          </h2>
          <p
            className="uxa-lead mb-10"
            style={{ color: 'var(--ink-2)', maxWidth: 520, margin: '18px auto 40px' }}
          >
            One free audit every month. White-label add-on available on all paid plans.
          </p>
          <div style={{ position: 'relative', display: 'inline-block', isolation: 'isolate' }}>
            <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex' }}>
            {user ? (
              <Link to="/audit/new" className="uxa-submit uxa-submit--auto">
                Run a Free Audit
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </Link>
            ) : (
              <button onClick={login} className="uxa-submit uxa-submit--auto">
                Run a Free Audit
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </button>
            )}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
